import { EnumTaskAssetsType } from '@/enums/task'
import type {
  CreationDuration,
  CreationEditorDraft,
  CreationFile,
  CreationForceTextToVideoType,
  CreationFormData,
  CreationRatio,
  IReqCreateVideoTask,
} from '@/types/apis/task'

const assetKeyMap: Record<EnumTaskAssetsType, 'image' | 'video' | 'audio' | 'text'> = {
  [EnumTaskAssetsType.IMAGE]: 'image',
  [EnumTaskAssetsType.VIDEO]: 'video',
  [EnumTaskAssetsType.AUDIO]: 'audio',
  [EnumTaskAssetsType.TEXT]: 'text',
}

const assetLabelMap: Record<EnumTaskAssetsType, string> = {
  [EnumTaskAssetsType.IMAGE]: '图',
  [EnumTaskAssetsType.VIDEO]: '视频',
  [EnumTaskAssetsType.AUDIO]: '音频',
  [EnumTaskAssetsType.TEXT]: '文本',
}

export interface CreationReference {
  token: string
  promptToken: string
  displayToken: string
  index: number
  type: EnumTaskAssetsType
}

export interface CreationPromptSegment {
  kind: 'text' | 'file'
  value: string
  file?: CreationFile
}

const promptTokenRegExp = /@\[(image|video|audio):(\d+)\]/gi

const splitSampleValues = (value?: string) =>
  value
    ?.split(',')
    .map(item => item.trim())
    .filter(Boolean) || []

export const normalizeRatio = (ratio: string): CreationRatio => {
  if (ratio === '9:16' || ratio === '4:3') {
    return ratio
  }

  return '16:9'
}

export const getCreationAssetLabel = (type: EnumTaskAssetsType) => assetLabelMap[type]

export const normalizeDuration = (duration: number): CreationDuration => {
  if (duration === 10 || duration === 15) {
    return duration
  }

  return 5
}

export const normalizeForceTextToVideoType = (type: number): CreationForceTextToVideoType => {
  if (type === 10) {
    return type
  }

  return 9
}

export const getOrderedSubmittedFiles = (files: CreationFile[]) => {
  return [...files]
    .filter(file => file.status === 'success' && file.longPath)
    .sort((left, right) => left.order - right.order)
}

export const buildCreationReferenceMap = (files: CreationFile[]) => {
  const orderedFiles = getOrderedSubmittedFiles(files)
  const counters = {
    [EnumTaskAssetsType.IMAGE]: 0,
    [EnumTaskAssetsType.VIDEO]: 0,
    [EnumTaskAssetsType.AUDIO]: 0,
    [EnumTaskAssetsType.TEXT]: 0,
  }

  return orderedFiles.reduce<Record<string, CreationReference>>((acc, file) => {
    const index = ++counters[file.type]
    const typeKey = assetKeyMap[file.type]

    acc[file.id] = {
      token: `${typeKey}:${index}`,
      promptToken: `@[${typeKey}:${index}]`,
      displayToken: `[${assetLabelMap[file.type]}${index}]`,
      index,
      type: file.type,
    }

    return acc
  }, {})
}

export const buildCreationReferenceLookup = (files: CreationFile[]) => {
  const referenceMap = buildCreationReferenceMap(files)

  return Object.entries(referenceMap).reduce<Record<string, CreationFile>>((acc, [fileId, ref]) => {
    const file = files.find(item => item.id === fileId)

    if (file) {
      acc[ref.token] = file
    }

    return acc
  }, {})
}

export const formatPromptReference = (prompt: string) => {
  return prompt.replace(/@\[(image|video|audio):(\d+)\]/gi, (_, type: string, index: string) => {
    const normalizedType = type.toLowerCase()
    if (normalizedType === 'image') {
      return `[图${index}]`
    }

    if (normalizedType === 'video') {
      return `[视频${index}]`
    }

    if (normalizedType === 'audio') {
      return `[音频${index}]`
    }

    return _
  })
}

export const parsePromptSegments = (prompt: string, files: CreationFile[]): CreationPromptSegment[] => {
  const lookup = buildCreationReferenceLookup(files)
  const segments: CreationPromptSegment[] = []
  let lastIndex = 0

  for (const match of prompt.matchAll(promptTokenRegExp)) {
    const fullMatch = match[0]
    const typeKey = (match[1] || '').toLowerCase() as 'image' | 'video' | 'audio'
    const index = match.index ?? 0

    if (index > lastIndex) {
      segments.push({
        kind: 'text',
        value: prompt.slice(lastIndex, index),
      })
    }

    const file = lookup[`${typeKey}:${match[2]}`]

    if (file) {
      segments.push({
        kind: 'file',
        value: fullMatch,
        file,
      })
    } else {
      segments.push({
        kind: 'text',
        value: fullMatch,
      })
    }

    lastIndex = index + fullMatch.length
  }

  if (lastIndex < prompt.length) {
    segments.push({
      kind: 'text',
      value: prompt.slice(lastIndex),
    })
  }

  return segments
}

export const buildDraftFromTask = (draft: {
  prompt: string
  files: CreationFile[]
  ratio: CreationRatio
  duration: number
  forceTextToVideoType?: number
  videoCategoryId?: number | null
}): CreationEditorDraft => {
  return {
    prompt: draft.prompt,
    files: draft.files.map(file => ({ ...file })),
    ratio: draft.ratio,
    duration: normalizeDuration(draft.duration),
    forceTextToVideoType: normalizeForceTextToVideoType(draft.forceTextToVideoType || 9),
    videoCategoryId: draft.videoCategoryId ?? undefined,
  }
}

export const buildCreateVideoTaskPayload = (formData: CreationFormData): IReqCreateVideoTask => {
  const orderedFiles = getOrderedSubmittedFiles(formData.files)
  const sampleImages = orderedFiles
    .filter(file => file.type === EnumTaskAssetsType.IMAGE)
    .map(file => file.longPath as string)
  const sampleVideos = orderedFiles
    .filter(file => file.type === EnumTaskAssetsType.VIDEO)
    .map(file => file.longPath as string)
  const sampleAudios = orderedFiles
    .filter(file => file.type === EnumTaskAssetsType.AUDIO)
    .map(file => file.longPath as string)

  return {
    AspectRatio: formData.ratio,
    Duration: formData.duration,
    ForceTextToVideoType: formData.forceTextToVideoType,
    GenerateAudio: true,
    IgnoreMaxConcurrentTasks: true,
    Prompt: formData.prompt,
    SampleAudios: sampleAudios,
    SampleImages: sampleImages,
    SampleVideos: sampleVideos,
    ...(formData.videoCategoryId !== undefined ? { VideoCategoryId: formData.videoCategoryId } : {}),
  }
}

export const buildRemoteCreationFiles = (
  taskId: number,
  prefix: 'task' | 'work',
  samples: {
    SampleImages?: string
    SampleVideos?: string
    SampleAudios?: string
  },
): CreationFile[] => {
  const imageFiles = splitSampleValues(samples.SampleImages).map((longPath, index) => ({
    id: `${prefix}-${taskId}-image-${index}`,
    type: EnumTaskAssetsType.IMAGE,
    label: `${assetLabelMap[EnumTaskAssetsType.IMAGE]}${index + 1}`,
    order: index,
    status: 'success' as const,
    longPath,
  }))

  const videoFiles = splitSampleValues(samples.SampleVideos).map((longPath, index) => ({
    id: `${prefix}-${taskId}-video-${index}`,
    type: EnumTaskAssetsType.VIDEO,
    label: `${assetLabelMap[EnumTaskAssetsType.VIDEO]}${index + 1}`,
    order: imageFiles.length + index,
    status: 'success' as const,
    longPath,
  }))

  const audioFiles = splitSampleValues(samples.SampleAudios).map((longPath, index) => ({
    id: `${prefix}-${taskId}-audio-${index}`,
    type: EnumTaskAssetsType.AUDIO,
    label: `${assetLabelMap[EnumTaskAssetsType.AUDIO]}${index + 1}`,
    order: imageFiles.length + videoFiles.length + index,
    status: 'success' as const,
    longPath,
  }))

  return [...imageFiles, ...videoFiles, ...audioFiles]
}
